import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getServiceFeeRates } from "../../services/lookupService.js";
import { money } from "../../utils/format.js";
import {
  ArrowRight, Sparkles, Clock, ShieldCheck, Lock, Truck, Zap, Save,
  Calculator, FileText, History, Send, UserPlus, Building2, FileSearch,
  CreditCard, CheckCircle2, BadgeCheck, Headphones, HelpCircle, FileCheck2, FileSpreadsheet,
} from "lucide-react";
import GradientButton from "../../components/marketing/landing/GradientButton.jsx";
import SeoHead from "../../seo/SeoHead.jsx";
import { ROUTE_META, PRICE_FROM } from "../../seo/siteMeta.js";
import { IRS_EFILE } from "../../data/compliance.js";
import {
  organizationSchema,
  webSiteSchema,
  softwareApplicationSchema,
  faqSchema,
} from "../../seo/schema.js";
import SectionEyebrow from "../../components/marketing/landing/SectionEyebrow.jsx";
import HeroMockup from "../../components/marketing/landing/HeroMockup.jsx";
import MarketingArt from "../../components/marketing/MarketingArt.jsx";
import IrsAuthorizedBadge from "../../components/marketing/IrsAuthorizedBadge.jsx";
import RoadTruck from "../../components/marketing/landing/RoadTruck.jsx";
import Reveal from "../../components/marketing/landing/Reveal.jsx";
import FeatureCard from "../../components/marketing/landing/FeatureCard.jsx";
import BenefitCard from "../../components/marketing/landing/BenefitCard.jsx";
import TimelineStep from "../../components/marketing/landing/TimelineStep.jsx";
import FaqAccordionItem from "../../components/marketing/landing/FaqAccordionItem.jsx";
import { RATES, WEIGHTS } from "../../data/rates.js";
import { nextDueDate } from "../../data/taxPeriods.js";

// The single-vehicle e-filing price, read live from service_fee_rate via the
// same public endpoint the /pricing page uses.
//
// Deliberately NOT hardcoded here. A hardcoded "$9.99" sat in this page's copy
// and in seo/siteMeta.js for weeks after the real price had moved to $9.45,
// and was only caught during the 2026-08-18 repricing - a hero is exactly
// where a stale price does the most damage, because it is the first number a
// filer sees and the one they will hold you to. Reading it costs one cached
// public GET and can never drift.
//
// Renders nothing until it resolves, and nothing at all if the API is
// unreachable or has no 1-vehicle band: no price in the hero is a cosmetic
// problem, the wrong price in the hero is a broken promise.
//
// Note this means the figure is absent from the react-snap prerendered HTML,
// which is built with no API reachable - same as every other live price on
// this site.
function useSingleVehiclePrice() {
  const [price, setPrice] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getServiceFeeRates()
      .then((rates) => {
        if (cancelled || !Array.isArray(rates)) return;
        const solo = rates.find(
          (r) => r.filingType === "original" && r.minVehicles === 1 && r.maxVehicles === 1
        );
        if (solo) setPrice(Number(solo.feeAmount));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return price;
}

const TRUST_TAGS = ["IRS TY2026 rate tables", "All 21 weight categories", "Logging vehicle rates"];

const WHO_MUST_FILE = [
  {
    image: "who-weight.webp", title: "55,000 lbs or more",
    body: "The vehicle has a taxable gross weight of 55,000 pounds or more and is used on public highways.",
  },
  {
    image: "who-registered.webp", title: "Registered in your name",
    body: "It's registered — or required to be registered — in your name under state, DC, Canadian, or Mexican law.",
  },
  {
    image: "who-first-used.webp", title: "First used this period",
    body: "Tax is due for the month the vehicle is first used during the July 1, 2026 – June 30, 2027 period.",
  },
];

const RATE_PREVIEW = ["A", "F", "M", "V"];

const FEATURES = [
  { icon: Send, image: "feat-efile.webp", title: "IRS E-File", description: "Transmit Form 2290 straight to the IRS and get your stamped Schedule 1 back electronically." },
  { icon: Zap, image: "feat-fast.webp", title: "Fast Filing", description: "A guided flow built for trucking — most returns are filed in well under ten minutes." },
  { icon: Lock, image: "feat-secure.webp", title: "Secure Payment", description: "Pay by direct debit, EFTPS, or card. Every transaction is encrypted end to end." },
  { icon: Save, image: "feat-draft.webp", title: "Save Draft", description: "Step away any time — your progress is saved so you can pick up right where you left off." },
  { icon: Truck, image: "feat-vehicles.webp", title: "Vehicle Management", description: "Add, edit, and track every truck's VIN, weight category, and status in one place." },
  // Verified against src/utils/bulkImport.js: MAX_ROWS = 500 is a per-FILE
  // cap - parseUploadedFile throws "please split it into batches of 500 or
  // fewer" rather than capping the return - and components/bulk/
  // BulkImportModal.jsx, which is shared by the trucks, VIN correction,
  // weight-increase and amended-return steps and appends on each import, so
  // repeated batches build up a fleet of any size. The wizard's only
  // vehicle-count cap is TrucksStep's rejected-resubmission rule (the
  // corrected return can't exceed the originally filed count), which does not
  // apply to a new return. "up to 500 at once" was dropped because it read as
  // a fleet ceiling and contradicted the 501+ service-fee band ($109.99).
  // Every claim here is a real capability - see the no-false-claims rule below.
  { icon: FileSpreadsheet, image: "feat-bulk.webp", title: "Bulk Upload", description: "Download an Excel template, fill in one row per vehicle, and import 500 at a time — as many batches as your fleet takes. For filings, VIN corrections, and amendments." },
  { icon: Calculator, image: "feat-calc.webp", title: "Tax Calculation", description: "Figured automatically from the real IRS TY2026 rate tables — logging and suspended rates included." },
  { icon: FileText, image: "feat-pdf.webp", title: "PDF Generation", description: "Download your completed Form 2290 and stamped Schedule 1 as ready-to-print PDFs." },
  { icon: History, image: "feat-history.webp", title: "Filing History", description: "Every past return, receipt, and Schedule 1 stays available whenever you need it again." },
  // "Mobile Friendly" removed 2026-08-07: mobile is not ready, so the claim was
  // false. Removing it also makes this a clean 3x3 grid instead of nine cards
  // plus one orphan on a fourth row.
];

const HOW_IT_WORKS = [
  { icon: UserPlus, image: "step-1-account.webp", title: "Create Account", description: "Sign up in seconds — just an email and a password to get started." },
  { icon: Building2, image: "step-2-business.webp", title: "Enter Business Details", description: "Your EIN, legal name, address, and signing authority for the return." },
  // Adding a truck and computing its tax is one step, not two: the tax is
  // figured from the IRS rate tables the moment a VIN and weight category are
  // entered, so splitting them implied a "calculate" action the user never takes.
  {
    icon: Truck,
    image: "step-3-trucks.webp",
    title: "Add Trucks & Get Tax",
    description:
      "Enter each vehicle's VIN and weight category — including logging and suspended trucks — and the tax is figured instantly from the IRS rate tables.",
  },
  { icon: FileSearch, image: "step-4-review.webp", title: "Review Return", description: "A final read-through of the whole return before anything is submitted." },
  { icon: CreditCard, image: "step-5-payment.webp", title: "Payment", description: "Pay the tax due by direct debit, EFTPS, or card — right inside the flow." },
  { icon: CheckCircle2, image: "step-6-submit.webp", title: "IRS Submission", description: "E-sign and transmit. Your stamped Schedule 1 is ready soon after acceptance." },
];

const BENEFITS = [
  { icon: ShieldCheck, image: "ben-secure.webp", title: "Secure Platform", description: "Your business and vehicle data is encrypted and protected at every step." },
  { icon: Calculator, image: "ben-accurate.webp", title: "Accurate Calculations", description: "Every figure is pulled from the real IRS TY2026 rate tables — no guesswork." },
  { icon: BadgeCheck, image: "ben-irs-ready.webp", title: "IRS Ready", description: "Built around Form 2290 and Schedule 1, exactly as the IRS expects them." },
  { icon: Sparkles, image: "ben-easy.webp", title: "Easy to Use", description: "A clean, guided flow that doesn't require a tax background to follow." },
  { icon: Zap, image: "ben-fast.webp", title: "Fast Processing", description: "Get your stamped Schedule 1 back in seconds, not days." },
  // "Responsive Design — a consistent experience whether you're on a desktop,
  // tablet, or phone" removed alongside "Mobile Friendly" above: it is the same
  // claim in different words, and equally untrue while mobile is not ready.
  { icon: Headphones, image: "ben-support.webp", title: "Customer Support", description: "Help is available whenever a filing question comes up." },
];

const FAQS = [
  {
    q: "Do I have to e-file?",
    a: "Yes, if you're reporting and paying tax on 25 or more vehicles during the period. Everyone else is encouraged to but isn't required.",
  },
  {
    q: "What if my truck is under 5,000 miles?",
    a: "You can claim suspension from the tax (7,500 miles for agricultural vehicles) — no tax due, but it still needs to be reported.",
  },
  {
    q: "Do logging vehicles get a lower rate?",
    a: "Yes — vehicles used exclusively to transport harvested forest products are taxed at a reduced rate in every weight category.",
  },
  {
    q: "How fast do I get my stamped Schedule 1?",
    a: "Usually within seconds of the IRS accepting your return — that's your proof of payment for DMV registration.",
  },
  {
    q: "Can I file for more than one business?",
    a: "Yes — each business you add keeps its own trucks, returns, and filing history under the same account.",
  },
  {
    q: "What payment methods are supported?",
    a: "Direct debit (EFW), EFTPS, or a credit/debit card. Form 2290 requires the tax to be paid in full when you file.",
  },
  // Brand questions (2026-10-22): answer "what is EasyFile2290" on the page
  // Google is most likely to show for the name. Same facts as /about.
  {
    q: "What is EasyFile2290?",
    a: `EasyFile2290 (easyfile2290.com) is an online service for filing IRS Form 2290, the Heavy Vehicle Use Tax return. A single-vehicle return starts at $${PRICE_FROM}, VIN corrections are free, and your stamped Schedule 1 usually comes back within seconds of IRS acceptance.`,
  },
  ...(IRS_EFILE.authorized
    ? [
        {
          q: "Is EasyFile2290 an IRS-authorized e-file provider?",
          a: "Yes. EasyFile2290 is on the IRS list of authorized Form 2290 e-file providers for tax year 2026, listed as Easyfile2290.com. Your return goes straight to the IRS and the stamped Schedule 1 comes back electronically.",
        },
      ]
    : []),
];

const scrollToFeatures = (e) => {
  e.preventDefault();
  document.getElementById("features")?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const meta = ROUTE_META["/"];

const Home = () => {
  const singleVehiclePrice = useSingleVehiclePrice();
  const nextDue = nextDueDate();
  return (
    <div className="overflow-hidden bg-[var(--land-bg)] text-[var(--land-ink)]">
      <SeoHead
        title={meta.title}
        description={meta.description}
        path="/"
        keywords={meta.keywords}
        schemas={[
          organizationSchema(),
          webSiteSchema(),
          softwareApplicationSchema(),
          // The FAQS array below actually renders on this page, so marking it
          // up is legitimate. Never emit FAQPage for FAQs that aren't visible.
          faqSchema(FAQS),
        ]}
      />
      {/* ============ HERO ============ */}
      {/* Editorial treatment: an oversized condensed headline carries the page
          and the illustration is cropped by this section's overflow so it runs
          off the right edge, instead of sitting in a tidy second column.
          Deliberately not the balanced split-hero rhythm form720-ui's landing
          page uses — the two products were reading as one brand.
          Plain white ground here (no .landing-mesh) so the type does the work;
          the mesh still runs on the Features section below. */}
      <div className="relative overflow-hidden">
        <div className="relative max-w-[1180px] mx-auto px-[22px] pt-[56px] pb-[44px]">
          <Reveal>
            {/* z-10: on lg the art is absolutely placed underneath and may
                overlap the copy's right edge at narrow desktop widths. */}
            <div className="relative z-10 lg:max-w-[620px]">
              <IrsAuthorizedBadge />

              {/* Brand inside the H1 (2026-10-22). The headline used to read only
                  "File Form 2290 online." - a phrase every competitor's page also
                  says - and Google was still rewriting "easyfile2290" to "easy file
                  2290". The H1 is the heaviest on-page signal, so the exact
                  one-word brand now opens it; the big three-line lockup is unchanged. */}
              <h1 className="font-disp uppercase font-bold text-[clamp(46px,7.2vw,92px)] leading-[.9] tracking-[-.02em] mt-5">
                <span className="block normal-case text-[clamp(20px,2.4vw,28px)] leading-none tracking-[.01em] text-[var(--land-muted)] mb-3">
                  EasyFile2290
                </span>
                File
                <br />
                Form 2290
                <br />
                <span className="text-[var(--land-primary)]">online.</span>
              </h1>

              <p className="text-[var(--land-muted)] mt-7 max-w-[46ch] text-[17px] leading-relaxed">
                {/* Brand named in visible text (2026-10-08): Google was auto-correcting
                    "easyfile2290" to "easy 2290". The logo alone is a weak signal. */}
                With EasyFile2290, figure your Heavy Highway Vehicle Use Tax with the real IRS TY2026 rate tables,
                e-file, and walk away with a stamped Schedule 1 — no paper, no waiting room.
              </p>

              {/* Price line. Says "single-vehicle return", NOT "per vehicle":
                  the fee is banded by how many vehicles are on the return, not
                  multiplied by them - 3 vehicles is one $23.99 return, not
                  3 x $5.99, and 24 vehicles is that same $23.99. "Per vehicle"
                  would understate the 2-24 bands and wildly overstate the
                  larger ones, and /pricing publishes the whole table right
                  underneath, so the two would visibly contradict each other. */}
              {singleVehiclePrice != null && (
                <div className="flex items-baseline gap-2.5 mt-7">
                  <span className="font-disp font-extrabold text-[34px] leading-none tracking-[-.02em] text-[var(--land-ink)]">
                    {money(singleVehiclePrice)}
                  </span>
                  <span className="text-[15px] text-[var(--land-muted)]">
                    to e-file a single-vehicle return
                  </span>
                </div>
              )}

              <div className="flex flex-wrap gap-3.5 mt-8">
                <GradientButton as={Link} to="/login?tab=up">
                  Start Filing <ArrowRight size={17} />
                </GradientButton>
                <GradientButton as="a" href="#features" variant="secondary" onClick={scrollToFeatures}>
                  Learn More
                </GradientButton>
              </div>

              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-8">
                {TRUST_TAGS.map((t) => (
                  <span key={t} className="flex items-center gap-1.5 text-[13px] font-medium text-[var(--land-ink)]">
                    <CheckCircle2 size={15} className="text-[var(--land-emerald)]" />
                    {t}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 pt-6 border-t border-[var(--land-line)]">
                <span className="flex items-center gap-2 text-[13px] text-[var(--land-muted)]">
                  <ShieldCheck size={16} className="text-[var(--land-primary)]" /> Bank-level security
                </span>
                <span className="flex items-center gap-2 text-[13px] text-[var(--land-muted)]">
                  <Lock size={16} className="text-[var(--land-primary)]" /> 256-bit encryption
                </span>
                <span className="flex items-center gap-2 text-[13px] text-[var(--land-muted)]">
                  <Clock size={16} className="text-[var(--land-primary)]" /> Filed in minutes
                </span>
              </div>
            </div>
          </Reveal>

          {/* Art. A SINGLE instance positioned by CSS, never rendered twice, so
              it is fetched once: it flows normally below the copy on small
              screens, then goes absolute on lg and bleeds past the container's
              right edge, cropped by the section's overflow-hidden.
              Generated PNG when present, the interactive mockup when not — see
              MarketingArt.jsx for why that fallback is a runtime check rather
              than a build-time import. */}
          {/* Framed image panel, right side. The art no longer bleeds off the
              edge: gpt-image-1 would not honour a transparent background, so a
              cut-out floating on white was never achievable. A bordered card
              that the photo fills edge to edge suits an opaque image far better
              — and it is what the strongest competitor hero in this market does.

              Still a SINGLE instance positioned by CSS: it flows under the copy
              on small screens, then goes absolute on lg. Never rendered twice,
              so it is fetched once. */}
          <div className="mt-12 lg:mt-0 lg:absolute lg:z-0 lg:top-1/2 lg:-translate-y-1/2 lg:right-0 lg:w-[46%] lg:max-w-[620px]">
            {/* No crop. An earlier version cropped the left edge to hide a
                garbled second trailer; the current single-truck image carries
                the "EasyFile2290" livery on that very edge, so cropping it
                would cut the brand name off. Show the full 3:2 frame. */}
            {/* Full uncropped 3:2 frame. Do not add object-cover crops here:
                a left-crop once sliced the trailer livery to "File2290", and a
                bottom-crop was rejected for shrinking the image. Anything wrong
                inside the frame gets fixed in the generation, not by cropping. */}
            <div className="relative rounded-[20px] overflow-hidden border border-[var(--land-line)] shadow-[0_24px_60px_rgba(18,22,31,.16)]">
              <MarketingArt
                src="form-2290-e-file-online.webp"
                alt="An EasyFile2290 truck parked at the entrance of the IRS building to file Form 2290 returns"
                eager
                className="w-full h-auto block"
                fallback={<HeroMockup />}
              />

              {/* Floating claim chips. Deliberately NOT volume statistics like
                  "50,000+ filings processed" — the fabricated homepage stats
                  were removed from this page for exactly that reason (D9).
                  Both of these are claims made elsewhere on the site already. */}
              {/* Deliberately NO overlays on this image.
                  Everything the hero needs to say is already inside the
                  photograph — the "EasyFile2290" trailer livery, the carved IRS
                  entrance, and the paper Form 2290 with pen. Two overlays were
                  tried and both covered something load-bearing: an HTML Form 2290
                  card hid the trailer branding, and a "From $9.99" price chip sat
                  on top of the carved "I" in IRS.
                  If a chip is ever added back, the safe zone is the lower-right
                  road area — the upper-left carries the IRS inscription and the
                  centre-left carries the brand. */}
            </div>
          </div>

          {/* Deadline banner. Colours come from the --amber tokens rather than
              the hardcoded #fde68a/#fffbeb/#92400e it used to carry, which were
              left over from the old warm palette and clashed once the theme
              moved to white/midnight/copper. */}
          {/* The deadline follows the calendar (nextDueDate) - it used to be a
              hardcoded "August 31, 2026", which was wrong from September on.
              Hidden entirely once the period's last deadline has passed. */}
          {nextDue && (
            <div className="relative z-10 mt-2">
              <div className="flex gap-3 items-start rounded-[14px] p-[15px_18px] text-[15px] border border-[color-mix(in_srgb,var(--amber)_45%,transparent)] bg-[var(--amber-soft)] text-[var(--ink)]">
                <Clock size={18} className="flex-none mt-0.5 text-[var(--amber)]" />
                <span>
                  <b>Next filing deadline: {nextDue.dueLabel}</b> — for vehicles first used on the road in{" "}
                  {nextDue.monthLabel}. Form 2290 is due by the last day of the month following first use.{" "}
                  <Link to="/form-2290-due-date-calculator" className="font-semibold text-[var(--accent)] underline underline-offset-2">
                    Find your deadline →
                  </Link>
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <RoadTruck />

      {/* ============ FEATURES ============ */}
      <div id="features" className="landing-mesh max-w-none px-[22px] py-[72px] scroll-mt-20">
        <div className="max-w-[1180px] mx-auto">
          <Reveal>
            <div className="text-center max-w-[640px] mx-auto">
              <SectionEyebrow center>Everything you need</SectionEyebrow>
              <h2 className="text-[30px] font-disp font-extrabold tracking-tight">
                Built for how trucking businesses actually file
              </h2>
              <p className="text-[var(--land-muted)] mt-2.5 text-[17px]">
                From your first truck to your stamped Schedule 1, every piece of the filing flow lives in one place.
              </p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 90}>
                <FeatureCard {...f} />
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* ============ HOW IT WORKS ============ */}
      <div className="bg-[var(--land-panel-2)] border-y border-[var(--land-line)]">
        <div className="max-w-[1180px] mx-auto px-[22px] py-[72px]">
          {/* Full-width header, then the steps laid out horizontally. This was
              a two-column layout with a sticky intro on the left and a vertical
              timeline on the right, which left a tall empty gutter under the
              intro once the timeline ran past it. */}
          <Reveal>
            <div className="text-center max-w-[640px] mx-auto">
              <SectionEyebrow center>How it works</SectionEyebrow>
              <h2 className="text-[clamp(26px,3.2vw,34px)] font-disp font-extrabold tracking-[-.02em]">
                Six steps to a stamped Schedule 1
              </h2>
              <p className="text-[var(--land-muted)] mt-2.5 text-[17px] leading-[1.6]">
                Every EasyFile2290 return follows the same guided flow — the same order the IRS asks for the
                information on Form 2290.
              </p>
            </div>
          </Reveal>

          {/* 6 steps over 3 columns wraps to a clean 3 + 3. TimelineStep's `perRow`
              must match this lg column count or its connector rules dangle
              where the grid wraps. */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10 mt-12">
            {HOW_IT_WORKS.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 80}>
                <TimelineStep {...s} index={i} total={HOW_IT_WORKS.length} perRow={3} />
              </Reveal>
            ))}
          </div>

          <div className="flex justify-center mt-12">
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-[var(--land-primary)] no-underline hover:gap-2.5 transition-[gap]"
            >
              Full walkthrough <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>

      {/* ============ WHY CHOOSE US ============ */}
      <div className="max-w-[1180px] mx-auto px-[22px] py-[72px]">
        <Reveal>
          <div className="text-center max-w-[640px] mx-auto">
            <SectionEyebrow center>Why choose us</SectionEyebrow>
            <h2 className="text-[30px] font-disp font-extrabold tracking-tight">
              A platform trucking businesses trust
            </h2>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.title} delay={(i % 3) * 80}>
              <BenefitCard {...b} />
            </Reveal>
          ))}
        </div>
      </div>

      {/* Who must file */}
      <div className="bg-[var(--land-panel-2)] border-y border-[var(--land-line)]">
        <div className="max-w-[1180px] mx-auto px-[22px] py-[72px]">
          <Reveal>
            <SectionEyebrow>Who must file</SectionEyebrow>
            <h2 className="text-[28px] font-disp font-extrabold tracking-tight">Three things decide it</h2>
            <p className="text-[var(--land-muted)] mt-2 max-w-[62ch] text-[16px]">
              Per the IRS Form 2290 instructions, you must file if a taxable highway motor vehicle meets all of the
              following.
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            {WHO_MUST_FILE.map((item, i) => (
              <Reveal key={item.title} delay={i * 90}>
                <div className="h-full bg-[var(--land-panel)] border border-[var(--land-line)] rounded-[16px] p-6 shadow-[var(--land-shadow)]">
                  <div className="w-9 h-9 rounded-[10px] bg-[color-mix(in_srgb,var(--land-primary)_10%,transparent)] text-[var(--land-primary)] grid place-items-center font-disp font-bold mb-3">
                    {i + 1}
                  </div>
                  <b className="font-disp text-[17px]">{item.title}</b>
                  <p className="text-[var(--land-muted)] text-sm mt-1.5">{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="flex gap-3 items-start rounded-[14px] p-[15px_18px] text-sm border border-[var(--land-line)] bg-[color-mix(in_srgb,var(--land-sky)_8%,var(--land-panel))] mt-4">
            <ShieldCheck size={18} className="flex-none mt-0.5 text-[var(--land-sky)]" />
            <span className="text-[var(--land-ink)]">
              Vans, pickups, and panel trucks are usually exempt — they're generally under the 55,000 lb threshold.
              Government, nonprofit, and Red Cross vehicles are exempt outright.
            </span>
          </div>
        </div>
      </div>

      {/* The "Trusted at scale / Numbers that back it up" section was removed on
          2026-08-07. It claimed 12,400+ vehicles filed, 3,200+ customers, 9,800+
          returns processed, and 5+ years of experience — none of it true; the
          product had not been deployed to production. Do not reintroduce usage
          or customer counts here until they are real and can be substantiated.
          The same rule covers ratings and testimonials: AggregateRating schema
          is only valid once genuine reviews exist. */}

      {/* Rates preview */}
      <div className="max-w-[1180px] mx-auto px-[22px] py-[72px]">
        <Reveal>
          <div className="flex justify-between items-end flex-wrap gap-4 mb-6">
            <div>
              <SectionEyebrow>Straight from Form 2290, page 2</SectionEyebrow>
              <h2 className="text-[28px] font-disp font-extrabold tracking-tight">Know the rate before you file</h2>
              <p className="text-[var(--land-muted)] mt-2 max-w-[62ch] text-[16px]">
                Tax is set by taxable gross weight category — 21 brackets from 55,000 lbs up. Logging vehicles pay a
                reduced rate in every bracket.
              </p>
            </div>
            <Link to="/rates" className="text-sm font-semibold text-[var(--land-primary)] no-underline flex items-center gap-1">
              See all 21 categories <ArrowRight size={15} />
            </Link>
          </div>

          <div className="bg-[var(--land-panel)] border border-[var(--land-line)] rounded-[16px] overflow-hidden shadow-[var(--land-shadow)]">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {["Category", "Taxable gross weight", "Standard (annual)", "Logging (annual)"].map((h) => (
                    <th
                      key={h}
                      className="text-left p-3.5 border-b border-[var(--land-line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--land-muted)] font-bold"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {RATE_PREVIEW.map((cat) => (
                  <tr key={cat}>
                    <td className="p-3.5 border-b border-[var(--land-line)] font-mono font-bold text-[var(--land-primary)]">{cat}</td>
                    <td className="p-3.5 border-b border-[var(--land-line)] text-[var(--land-muted)]">{WEIGHTS[cat]} lb</td>
                    <td className="p-3.5 border-b border-[var(--land-line)] font-mono">{money(RATES[cat][0])}</td>
                    <td className="p-3.5 border-b border-[var(--land-line)] font-mono">{money(RATES[cat][1])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>

      {/* ============ FAQ ============ */}
      <div className="bg-[var(--land-panel-2)] border-y border-[var(--land-line)]">
        <div className="max-w-[1180px] mx-auto px-[22px] py-[72px]">
          <Reveal>
            <div className="flex justify-between items-end flex-wrap gap-4 mb-8">
              <div>
                <SectionEyebrow icon={HelpCircle}>Common questions</SectionEyebrow>
                <h2 className="text-[28px] font-disp font-extrabold tracking-tight">
                  Answered straight from the instructions
                </h2>
              </div>
              <Link to="/faq" className="text-sm font-semibold text-[var(--land-primary)] no-underline flex items-center gap-1">
                All FAQs <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>

          <div className="grid gap-3 max-w-[820px] mx-auto">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 60}>
                <FaqAccordionItem q={f.q} a={f.a} defaultOpen={i === 0} />
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* ============ FINAL CTA ============ */}
      <div className="max-w-[1180px] mx-auto px-[22px] py-[72px]">
        <Reveal>
          <div className="landing-aurora-deep relative overflow-hidden rounded-[24px] p-[48px_34px] text-center">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_70%_at_22%_25%,color-mix(in_srgb,var(--land-primary)_40%,transparent),transparent)] pointer-events-none" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_70%_at_80%_75%,color-mix(in_srgb,var(--land-indigo)_35%,transparent),transparent)] pointer-events-none" />

            <div className="relative max-w-[600px] mx-auto">
              <p className="inline-flex items-center justify-center gap-2 text-[13px] font-bold text-white/60 uppercase tracking-[.1em] mb-4">
                <FileCheck2 size={14} /> Ready when you are
              </p>
              <h2 className="text-[clamp(26px,3.2vw,36px)] font-disp font-extrabold tracking-tight text-white">
                Start your TY2026 Form 2290
              </h2>
              <p className="text-white/75 mt-3 leading-relaxed max-w-[480px] mx-auto">
                Create an account, add your trucks, and file — your stamped Schedule 1 is usually ready within
                seconds of acceptance.
              </p>

              <div className="flex items-center justify-center gap-3.5 flex-wrap mt-8">
                <GradientButton as={Link} to="/login?tab=up" variant="white">
                  Get started free <ArrowRight size={17} />
                </GradientButton>
              </div>

              <div className="flex justify-center gap-6 flex-wrap mt-7">
                <span className="flex items-center gap-1.5 text-[13px] text-white/60">
                  <Lock size={14} /> 256-bit SSL encryption
                </span>
                <span className="flex items-center gap-1.5 text-[13px] text-white/60">
                  <BadgeCheck size={14} /> IRS TY2026 rate tables
                </span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default Home;
